import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from './use-debounce';

export const SEARCH_PARAM_DEFAULT_KEY = 'q';
const DEFAULT_DELAY_MS = 300;

export interface UseSearchParamStateOptions {
  /** Pause after the last keystroke before the URL (and `query`) update. Default: 300 */
  delay?: number;
}

export interface UseSearchParamStateReturn {
  /** What the box shows. Bind this to the input's `value`. */
  input: string;
  /** Bind this to the input's `onChange`. */
  setInput: (value: string) => void;
  /** The committed, trimmed search read from the URL. Feed this to the data query. */
  query: string;
}

/**
 * A list page's search box, kept in the URL so opening a row and pressing Back
 * returns to the same filtered list (and the view can be linked or refreshed).
 *
 * The box updates as you type; the URL follows once typing pauses. Writes replace
 * the history entry, so Back leaves the page instead of stepping through every
 * search, and each new search drops `page` so results start from page 1.
 */
export function useSearchParamState(
  key: string = SEARCH_PARAM_DEFAULT_KEY,
  options?: UseSearchParamStateOptions,
): UseSearchParamStateReturn {
  const delay = options?.delay ?? DEFAULT_DELAY_MS;
  const [searchParams, setSearchParams] = useSearchParams();
  const urlValue = searchParams.get(key) ?? '';

  const [input, setInput] = useState(urlValue);
  const debouncedInput = useDebounce(input, delay);

  // The last value this hook put in the URL (or saw there). Tells "the URL changed
  // under the box" apart from "the URL caught up with what was typed".
  const applied = useRef(urlValue);

  // setSearchParams is not referentially stable; the ref keeps the commit effect
  // from re-running every time any other param changes.
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  useEffect(() => {
    // Back/forward or a link changed the URL under the box.
    if (urlValue === applied.current) return;
    applied.current = urlValue;
    setInput(urlValue);
  }, [urlValue]);

  useEffect(() => {
    const next = debouncedInput.trim();
    if (next === applied.current) return;
    applied.current = next;
    setSearchParamsRef.current(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next) params.set(key, next);
        else params.delete(key);
        params.delete('page');
        return params;
      },
      { replace: true },
    );
  }, [debouncedInput, key]);

  return { input, setInput, query: urlValue };
}

export default useSearchParamState;
